import "../css/WishButton.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { gachaConfig } from "../config/gacha";
import { themeConfig } from "../config/theme";

function getProgressCopy({ hasTarget, progressCount, maxDraws, resultPayload }) {
    if (resultPayload?.wasTargetHit) {
        return themeConfig.pityCopy.hitTarget;
    }

    if (resultPayload?.star === 5 && !resultPayload?.targetItem) {
        return themeConfig.pityCopy.freshFiveStar;
    }

    const left = Math.max(maxDraws - progressCount, 1);
    if (hasTarget) {
        return themeConfig.pityCopy.targeted(left);
    }

    return progressCount > 0 ? themeConfig.pityCopy.normal(left) : themeConfig.pityCopy.idle;
}

export default function WishButton({
    getColor,
    onWish,
    onOpenDetail,
    wishLabel,
    progressCount,
    maxDraws,
    hasTarget,
    loading,
    luckCopy,
    sessionSummary,
    totalDraws,
    fiveStarStreak,
}) {
    const [disabled, setDisabled] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [showResult, setShowResult] = useState(false);
    const [resultPayload, setResultPayload] = useState(null);
    const audioRef = useRef(null);
    const resultSoundRef = useRef(null);

    const progressCopy = useMemo(
        () => getProgressCopy({ hasTarget, progressCount, maxDraws, resultPayload }),
        [hasTarget, progressCount, maxDraws, resultPayload],
    );

    useEffect(() => {
        if (isPlaying && audioRef.current) {
            audioRef.current.play().catch((error) => {
                console.warn("Autoplay failed:", error);
            });
        }
    }, [isPlaying]);

    useEffect(() => {
        if (showResult && resultSoundRef.current) {
            resultSoundRef.current.play().catch((error) => {
                console.warn("Autoplay failed:", error);
            });
        }
    }, [showResult]);

    function handleClick() {
        const payload = onWish();
        if (!payload?.result) {
            return;
        }

        setDisabled(true);
        setResultPayload(payload);
        setShowResult(false);
        setIsPlaying(true);
    }

    function handleEnding() {
        setIsPlaying(false);
        setDisabled(false);
        setShowResult(true);
    }

    function handleClose() {
        setShowResult(false);
        setDisabled(false);
        setResultPayload(null);
        setIsPlaying(false);
    }

    const currentResult = resultPayload?.result;
    const currentStar = resultPayload?.star ?? 3;
    const resultSound = gachaConfig.media.resultSoundByStar[currentStar];
    const resultCopy = currentResult?.resultCopy ?? resultPayload?.category?.resultCopy?.[currentStar] ?? '';

    const videoOverlay = isPlaying && resultPayload
        ? createPortal(
            <>
                <audio ref={audioRef} src={gachaConfig.media.drawSound} preload="auto" />
                <video
                    className="gachaVideo"
                    autoPlay
                    muted
                    playsInline
                    controls={false}
                    src={gachaConfig.media.videoByStar[currentStar]}
                    onEnded={handleEnding}
                />
                <button
                    className="skipButton"
                    onClick={() => {
                        if (audioRef.current) {
                            audioRef.current.pause();
                        }
                        handleEnding();
                    }}
                >
                    跳过
                </button>
            </>,
            document.body,
        )
        : null;

    const resultOverlay = showResult && currentResult
        ? createPortal(
            <div className="result-windows">
                <div className="showResult">
                    <audio ref={resultSoundRef} src={resultSound} preload="auto" />
                    <div className="result-copy-box">
                        <div className="result-copy-tag">{resultPayload.category.label} · {currentStar} 星结果</div>
                        <div className="result-copy-main">{resultCopy}</div>
                        {currentResult.subtitle && <div className="result-copy-subtitle">{currentResult.subtitle}</div>}
                        {currentResult.tags?.length ? (
                            <div className="result-tags">
                                {currentResult.tags.map((tag) => (
                                    <span key={tag} className="result-tag">{tag}</span>
                                ))}
                            </div>
                        ) : null}
                    </div>
                    <img className="showPicture" alt={currentResult.name} src={currentResult.image} />
                    <div className="showData">
                        <div className="foodsName">
                            <div className="foodName-p" style={{ color: getColor(currentResult.star) }}>
                                {currentResult.name}
                            </div>
                        </div>
                        <div className="showStar">
                            {Array.from({ length: currentResult.star }).map((_, index) => (
                                <span
                                    key={index}
                                    className="showStar star-animate"
                                    style={{ color: getColor(currentResult.star) }}
                                >
                                    ★
                                </span>
                            ))}
                        </div>
                        <div className="result-action-row">
                            {currentResult.detailUrl ? (
                                <button
                                    className="detailButton"
                                    onClick={() => onOpenDetail(currentResult)}
                                >
                                    立即看做法
                                </button>
                            ) : null}
                            <button className="closeButton" onClick={handleClose}>
                                收下今日安排
                            </button>
                        </div>
                    </div>
                </div>
            </div>,
            document.body,
        )
        : null;

    return (
        <>
            <div className="wish-action-box">
                <div className="wish-status-board">
                    <div className="wish-status-pill">{luckCopy}</div>
                    <div className="wish-status-subtitle">{sessionSummary}</div>
                    <div className="wish-status-stats">
                        <span>本分类已祈愿 {totalDraws} 次</span>
                        <span>连续未出五星 {fiveStarStreak} 次</span>
                    </div>
                </div>
                <button className="wishButton" onClick={handleClick} disabled={disabled || loading}>
                    {loading ? '卡池准备中...' : wishLabel}
                </button>
                <div className="wish-progress-copy">{progressCopy}</div>
            </div>
            {videoOverlay}
            {resultOverlay}
        </>
    );
}
